import * as React from "react";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";

import { CallLog } from "@/hooks/api/use-call-logs";

interface CallInsightsPanelProps {
	callLog: CallLog | null;
	/**
	 * Drops the operator-only fields. A disposition code, a telephony clearing reason and a
	 * "coming soon" chip are things the people running the platform need; a business reading back
	 * a call about its own customer needs what happened and what was learned.
	 */
	plain?: boolean;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<Box>
			<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
				{label}
			</Typography>
			{children}
		</Box>
	);
}

export function CallInsightsPanel({ callLog, plain = false }: CallInsightsPanelProps): React.JSX.Element {
	const metadata = (callLog?.metadata ?? {}) as Record<string, any>;
	const summary: string | undefined = metadata.summary;
	const disposition: string | undefined = metadata.disposition;
	const reason: string | undefined = metadata.reason;
	const isAnalysing = Boolean(metadata.enqueuedForAnalysis);
	const extra: Record<string, any> | undefined =
		metadata.extra && typeof metadata.extra === "object" ? metadata.extra : undefined;
	const extraEntries = extra ? Object.entries(extra).filter(([, value]) => value !== "" && value != null) : [];

	return (
		<Box
			sx={{
				width: { xs: "100%", md: 280 },
				flexShrink: 0,
				borderRight: { md: "1px solid var(--mui-palette-divider)" },
				borderBottom: { xs: "1px solid var(--mui-palette-divider)", md: "none" },
				bgcolor: "var(--mui-palette-background-level1)",
				p: 2,
				overflowY: "auto",
			}}
		>
			<Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: "0.05em" }}>
				{plain ? "About this call" : "Call Insights"}
			</Typography>

			<Stack spacing={2} sx={{ mt: 1.5 }}>
				<Field label={plain ? "What happened" : "AI Summary"}>
					{isAnalysing ? (
						<Typography variant="body2" color="text.secondary" fontStyle="italic">
							Analysing…
						</Typography>
					) : summary ? (
						<Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
							{summary}
						</Typography>
					) : (
						<Typography variant="body2" color="text.secondary">
							No summary available
						</Typography>
					)}
				</Field>

				{plain ? null : (
					<Field label="Disposition">
						{disposition ? (
							<Chip label={disposition} size="small" variant="outlined" color="primary" />
						) : (
							<Typography variant="body2" color="text.secondary">
								—
							</Typography>
						)}
					</Field>
				)}

				{plain ? null : (
					<Field label="Sentiment">
						<Chip
							label="Coming soon"
							size="small"
							variant="outlined"
							sx={{
								color: "var(--mui-palette-text-secondary)",
								opacity: 0.6
							}}
						/>
					</Field>
				)}

				{extraEntries.length > 0 && (
					<Field label={plain ? "What we picked up" : "Extracted fields"}>
						<Stack spacing={0.5}>
							{extraEntries.map(([key, value]) => (
								<Box key={key} sx={{ display: "flex", gap: 0.5 }}>
									<Typography variant="caption" color="text.secondary">
										{key}:
									</Typography>
									<Typography variant="caption" sx={{ wordBreak: "break-word" }}>
										{String(value)}
									</Typography>
								</Box>
							))}
						</Stack>
					</Field>
				)}

				{reason && !plain && (
					<>
						<Divider />
						<Field label="Status reason">
							<Typography variant="caption" color="text.secondary">
								{reason}
							</Typography>
						</Field>
					</>
				)}
			</Stack>
		</Box>
	);
}
